/**
 * advisorClient — the OFFICE transport behind the shared AskOrSearch
 * component (plan 05-10; re-anchored to the owned suite in 10-06).
 *
 * "One engine, two entry points" (05-CONTEXT): the AskOrSearch folder is a
 * verbatim COPY of harmony-qms-fieldlink's component; only the injected
 * transport differs per surface. This file is the office/Entra path:
 *
 * - postChat: drives the Q&S Advisor's client-driven tool loop DIRECTLY from
 *   the browser (the same way the Advisor's own SPA does — one POST per
 *   Claude round, append `new_messages`, re-POST until `done`). The Advisor
 *   is called with `X-Auth-Token: <Entra access token>` whose audience is
 *   the Advisor app (`53702256…`) — no service token, office users have
 *   Entra identities. The Sources section is then parsed out of the answer
 *   and citations are rewritten to register docIds (POL-###/PRC-###),
 *   mirroring the fieldlink /api/chat pipeline but WITHOUT tier gating:
 *   office staff may open everything.
 * - searchLibrary: reads the Policy + Process Registers via the dashboard's
 *   existing delegated-Graph client (Sites.ReadWrite.All — the signed-in
 *   user's own Q&S-site access).
 * - openDoc: resolves a register id to the published library file's webUrl
 *   (direct open in a new tab — office staff have site access; no bytes
 *   proxy, no watermark — a DELIBERATE divergence from FIELDLINK-CONTRACTS
 *   §6's proxied-bytes rule, which is the field surface's). It never opens
 *   Centro: since 13.1-10 (ISS-229 b) a row whose own file can't be resolved
 *   is not openable, and its CentroUrl is offered only as the labelled
 *   secondary "Old copy in Centro" link.
 *
 * The pure half — spine contracts, index building, the Sources parser and
 * the citation matcher — lives in `./advisorIndex.ts` (no MSAL, no Graph) so
 * it is unit-tested; this file owns tokens, Graph reads, the 10-minute
 * cache and `openDoc`. Both surfaces key visibility on the owned
 * `ApprovalStatus` allowlist (ISS-131, D-10-03-1 a); Centro's
 * `PolicyStatus` / `ProcessStatus` are trap column T3 and are not fetched.
 *
 * Provisioning (05-11, measured 2026-09-22 — D-10-06-4): the Advisor app
 * exposes `access_as_user`, the dashboard SP (`94e90ab8…`) holds the
 * admin-consented grant on the Advisor SP, and the Advisor Function App's
 * CORS allows `https://qms.harmonyhomecare.com.au`. What 05-11 left undone
 * was this app's OWN Content-Security-Policy: `staticwebapp.config.json`'s
 * `connect-src` must name the Advisor host or the browser refuses the fetch
 * before any token matters (ISS-142, fixed in 10-06). If token acquisition
 * still fails, postChat throws AdvisorAuthError — LookupPage renders the
 * documented "not yet enabled" state (library search still works, it only
 * needs Graph).
 */

import type { IPublicClientApplication } from '@azure/msal-browser';
import { graphScopes } from '../auth/authConfig';
import { acquireToken } from '../auth/tokens';
import type { Client } from '@microsoft/microsoft-graph-client';
import {
  createGraphClient,
  errorStatus,
  getSiteId,
  getListId,
  listItems,
} from './graphClient';
import type {
  ChatMessage,
  ChatResponse,
  LibraryMatch,
} from '../components/AskOrSearch';
import {
  SPINES,
  REGISTER_SELECT,
  answerCitations,
  buildIndex,
  normalizeKey,
  scrubUrls,
  searchIndex,
  splitExtension,
  splitSources,
} from './advisorIndex';
import type {
  CorpusFile,
  LibraryFailure,
  LibraryStatus,
  LookupIndex,
  RegisterRow,
} from './advisorIndex';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

/** The live Q&S Advisor API (harmony-semantic-search FA). */
const ADVISOR_URL = (
  import.meta.env.VITE_ADVISOR_URL ||
  'https://harmony-qs-advisor-api-eua5a0gbfbephhb5.australiasoutheast-01.azurewebsites.net'
).replace(/\/+$/, '');

/** The Advisor's Entra app id — the token audience its backend validates. */
const ADVISOR_APP_ID =
  import.meta.env.VITE_ADVISOR_APP_ID || '53702256-76a1-4654-82ae-62d8c277f40c';

/**
 * The Q&S corpus document library drive (`Q&S Agent (Knowledge Navigator)`),
 * verified in FIELDLINK-CONTRACTS.md §6, with its root corpus folders.
 */
const CORPUS_DRIVE_ID =
  import.meta.env.VITE_QS_CORPUS_DRIVE_ID ||
  'b!RLi5pcTYYEaO99TG2zKKisWnTwol-3pCiDKVw5yViz53d4clPwP0RomqoFc0i5w6';

const MAX_ROUNDS = 6;
const INDEX_TTL_MS = 10 * 60 * 1000;

// ---------------------------------------------------------------------------
// Advisor token acquisition (the office/Entra path)
// ---------------------------------------------------------------------------

/**
 * Thrown when an Advisor token cannot be acquired — almost always because the
 * 05-11 Entra grant (dashboard app → Advisor API permission + admin consent)
 * has not been provisioned yet. LookupPage keys its "Ask not yet enabled"
 * state off this class.
 */
export class AdvisorAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AdvisorAuthError';
  }
}

/**
 * Acquire an access token for the Advisor API (audience = the Advisor app).
 * Silent first, then the shared popup (`../auth/tokens`, 13.1-13). The Advisor's
 * backend accepts both `{appId}` and `api://{appId}` audiences (its auth.py),
 * so the `.default` resource form is sufficient. The account is resolved at
 * call time from the MSAL instance (no render-time state needed). Any failure
 * is an AdvisorAuthError, which LookupPage shows as its "Advisor blocked" banner.
 */
export async function acquireAdvisorToken(
  instance: IPublicClientApplication,
): Promise<string> {
  const account = instance.getAllAccounts()[0] ?? null;
  if (!account) {
    throw new AdvisorAuthError('No authenticated account.');
  }
  try {
    return await acquireToken(instance, { scopes: [`${ADVISOR_APP_ID}/.default`], account });
  } catch (error) {
    throw new AdvisorAuthError(
      `Advisor token acquisition failed: ${(error as Error).message}`,
    );
  }
}

/**
 * Acquire a Graph token from the MSAL instance directly (same scopes and
 * shared silent → popup path as useAuth().getGraphToken, but callable from a
 * non-hook context so the transport can be built once per page mount).
 */
export async function acquireGraphToken(
  instance: IPublicClientApplication,
): Promise<string> {
  const account = instance.getAllAccounts()[0] ?? null;
  if (!account) {
    throw new Error('No authenticated account.');
  }
  return acquireToken(instance, { ...graphScopes, account });
}

// ---------------------------------------------------------------------------
// Graph reads — the corpus library folders
// ---------------------------------------------------------------------------

interface GraphDriveChild {
  id: string;
  name?: string;
  webUrl?: string;
  file?: unknown;
}

/**
 * Read one corpus folder. A failed read is REPORTED, not swallowed (ISS-229 b,
 * 13.1-10): until 6 Oct it returned `[]`, so every row silently fell back to
 * its CentroUrl — the whole suite, for anyone outside the Q&S site's group.
 * Now the failure (folder + status) reaches the index, which marks the
 * library unreachable, and the page says so.
 */
export async function listCorpusFolder(
  client: Client,
  siteId: string,
  folder: string,
): Promise<{ files: CorpusFile[]; failure: LibraryFailure | null }> {
  const out: CorpusFile[] = [];
  try {
    let response = (await client
      .api(
        `/sites/${siteId}/drives/${CORPUS_DRIVE_ID}/root:/${encodeURIComponent(folder)}:/children`,
      )
      .select('id,name,webUrl,file')
      .get()) as { value: GraphDriveChild[]; '@odata.nextLink'?: string };
    for (;;) {
      for (const child of response.value || []) {
        if (!child || !child.file || !child.name || !child.webUrl) continue;
        const { stem } = splitExtension(child.name);
        const key = normalizeKey(stem);
        if (!key) continue;
        out.push({ name: child.name, folder, webUrl: child.webUrl, key });
      }
      const next = response['@odata.nextLink'];
      if (!next) break;
      response = (await client.api(next).get()) as {
        value: GraphDriveChild[];
        '@odata.nextLink'?: string;
      };
    }
  } catch (error) {
    // The lookup surface still works (search, Ask), but rows in this folder
    // can't open our copy; the caller learns which folder and why.
    return { files: out, failure: { folder, status: errorStatus(error) } };
  }
  return { files: out, failure: null };
}

// ---------------------------------------------------------------------------
// Advisor chat loop (client-driven, like the Advisor's own SPA)
// ---------------------------------------------------------------------------

/** Advisor messages grow tool_use/tool_result content blocks — keep opaque. */
interface AdvisorMessage {
  role: string;
  content: unknown;
}

interface AdvisorRound {
  done?: boolean;
  content?: string;
  new_messages?: AdvisorMessage[];
}

async function driveAdvisorLoop(
  token: string,
  messages: ChatMessage[],
): Promise<{ content: string; truncated: boolean }> {
  let apiMessages: AdvisorMessage[] = messages.map((m) => ({
    role: m.role,
    content: m.content,
  }));
  let bestPartial = '';

  for (let round = 0; round < MAX_ROUNDS; round++) {
    const response = await fetch(`${ADVISOR_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'X-Auth-Token': token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages: apiMessages, stream: false }),
    });
    if (response.status === 401 || response.status === 403) {
      throw new AdvisorAuthError(`Advisor rejected the token (${response.status})`);
    }
    if (!response.ok) {
      throw new Error(`Advisor error (${response.status})`);
    }
    const body = (await response.json()) as AdvisorRound;

    if (typeof body.content === 'string' && body.content.trim()) {
      bestPartial = body.content;
    }
    if (body.done === true) {
      return { content: bestPartial, truncated: false };
    }
    const newMessages = Array.isArray(body.new_messages) ? body.new_messages : [];
    if (newMessages.length === 0) break; // defensive: no progress → stop
    apiMessages = [...apiMessages, ...newMessages];
  }
  return { content: bestPartial, truncated: true };
}

// ---------------------------------------------------------------------------
// Transport factory
// ---------------------------------------------------------------------------

export interface AdvisorTransportDeps {
  /** Access token for the Advisor API (audience = the Advisor app). */
  getAdvisorToken: () => Promise<string>;
  /** Access token for Microsoft Graph (the dashboard's existing scopes). */
  getGraphToken: () => Promise<string>;
  /**
   * Told on every index build whether our policy library could be read
   * (ISS-229 b) — LookupPage shows its notice from this. A failed build
   * (the registers themselves unreadable) reports unreachable too.
   */
  onLibraryStatus?: (status: LibraryStatus) => void;
}

export interface AdvisorTransport {
  postChat: (messages: ChatMessage[]) => Promise<ChatResponse>;
  searchLibrary: (q: string) => Promise<LibraryMatch[]>;
  openDoc: (docId: string) => void;
}

const EMPTY_PARTIAL_FALLBACK =
  'Sorry — that question needed more searching than one go allows. ' +
  'Try asking something more specific, or check the matching documents below.';

/**
 * Build the office transport for the shared AskOrSearch component.
 * One instance per page mount; the lookup index is cached per instance
 * (10-minute TTL) so ask + search + open share one Graph sweep.
 */
export function createAdvisorTransport(deps: AdvisorTransportDeps): AdvisorTransport {
  let indexCache: { index: LookupIndex; builtAt: number } | null = null;
  let indexPromise: Promise<LookupIndex> | null = null;

  async function buildLookupIndex(): Promise<LookupIndex> {
    const client = createGraphClient(deps.getGraphToken);
    const siteId = await getSiteId(client);
    const [policyListId, processListId] = await Promise.all([
      getListId(client, siteId, SPINES.policy.listTitle),
      getListId(client, siteId, SPINES.process.listTitle),
    ]);
    // The owned governance columns travel with the row (ISS-131 / D-10-06-1);
    // Centro's status columns are not in the select at all.
    const select = [...REGISTER_SELECT];
    const folderNames = new Set<string>();
    for (const spine of [SPINES.policy, SPINES.process]) {
      folderNames.add(spine.folders.ndis);
      folderNames.add(spine.folders.agedCare);
    }
    const [policyRows, processRows, ...folderReads] = await Promise.all([
      listItems<RegisterRow>(client, siteId, policyListId, { select }),
      listItems<RegisterRow>(client, siteId, processListId, { select }),
      ...Array.from(folderNames, (folder) => listCorpusFolder(client, siteId, folder)),
    ]);
    const failures = folderReads.flatMap((r) => (r.failure ? [r.failure] : []));
    if (failures.length > 0) {
      console.warn(
        `[advisorClient] QMS policy library unreachable for this account: ${failures
          .map((f) => `${f.folder} (${f.status ?? 'no status'})`)
          .join(', ')}`,
      );
    }
    return buildIndex(
      { policy: policyRows.items, process: processRows.items },
      folderReads.flatMap((r) => r.files),
      failures,
    );
  }

  async function ensureIndex(): Promise<LookupIndex> {
    if (indexCache && Date.now() - indexCache.builtAt < INDEX_TTL_MS) {
      return indexCache.index;
    }
    if (!indexPromise) {
      indexPromise = buildLookupIndex()
        .then((index) => {
          indexCache = { index, builtAt: Date.now() };
          deps.onLibraryStatus?.({
            reachable: index.libraryReachable,
            failures: index.libraryFailures,
          });
          return index;
        })
        .catch((error: unknown) => {
          deps.onLibraryStatus?.({
            reachable: false,
            failures: [{ folder: 'Policy and Process Registers', status: errorStatus(error) }],
          });
          throw error;
        })
        .finally(() => {
          indexPromise = null;
        });
    }
    return indexPromise;
  }

  async function postChat(messages: ChatMessage[]): Promise<ChatResponse> {
    // Token + index in parallel: the index is needed for the citation
    // rewrite anyway, and failing early on auth beats a wasted loop.
    const [token] = await Promise.all([
      deps.getAdvisorToken(),
      // Index failure must not sink the answer — degrade to text citations.
      ensureIndex().catch(() => null),
    ]);
    const { content, truncated } = await driveAdvisorLoop(token, messages);

    const { body, sourcesBlock } = splitSources(content);
    let index: LookupIndex | null = null;
    try {
      index = await ensureIndex();
    } catch {
      index = null;
    }
    // The Sources block's chips — or, when it is missing or matches nothing,
    // the references the answer names in its body (13.1-10).
    const citations = answerCitations(body, sourcesBlock, index);
    return {
      content: scrubUrls(body).trim() || (truncated ? EMPTY_PARTIAL_FALLBACK : ''),
      citations,
      truncated,
    };
  }

  async function searchLibrary(q: string): Promise<LibraryMatch[]> {
    const index = await ensureIndex();
    if (!q.trim()) return [];
    // Keywords, not the whole sentence (13.1-10): see advisorIndex.searchIndex.
    return searchIndex(index, q);
  }

  function openDoc(docId: string): void {
    // Open the tab synchronously (popup blockers), then point it at the
    // resolved target. Only 'available' docs carry an openable affordance,
    // so the index lookup should always land — close the tab if it doesn't.
    const win = window.open('', '_blank');
    void ensureIndex()
      .then((index) => {
        const entry = index.byId.get(String(docId).toUpperCase());
        if (entry?.openUrl) {
          if (win) win.location.href = entry.openUrl;
        } else if (win) {
          win.close();
        }
      })
      .catch(() => {
        if (win) win.close();
      });
  }

  return { postChat, searchLibrary, openDoc };
}
